/**
 * A station's colours.
 *
 * Every berth belongs to somebody, and until now the only sign of it was a
 * badge on the deck. Colouring the console the operator's colour means you know
 * whose dock you are standing on before you have read anything — and it means
 * arriving somewhere new *looks* like arriving somewhere new, which is most of
 * what makes a galaxy feel inhabited rather than procedurally identical.
 *
 * Built from one hue apiece rather than a hand-picked palette per faction: five
 * factions times six roles is thirty colours to keep in step, and a derived set
 * cannot drift.
 */

import { FACTIONS, type FactionId } from '@interspacial/shared';

export interface StationTheme {
  /** Who this is, ready to print. */
  operator: string;
  /**
   * Short form, when there is one.
   *
   * Null for the factions that have no abbreviation in the data — the Deep
   * Cheese Front and the rogues both do. Falling back to a placeholder made
   * them read as unaligned, which they emphatically are not.
   */
  abbreviation: string | null;
  /** The faction's colour, at full strength. Titles and lit controls. */
  accent: string;
  /** Body text. The accent, desaturated far enough to read as neutral. */
  text: string;
  dim: string;
  edge: string;
  /** Panel fill. Nearly black, tinted so it is not the same black everywhere. */
  backing: string;
  /** A lit control's fill. */
  active: string;
}

/**
 * Hue and saturation per faction, in degrees and per cent.
 *
 * Taken from `ALLEGIANCE_COLORS`, which is what the map and the deck plating
 * already use — the console being a different green from the station it is
 * bolted to would be worse than no colour at all.
 */
const HUES: Record<FactionId | 'unaligned', { hue: number; saturation: number }> = {
  red: { hue: 356, saturation: 100 },
  blue: { hue: 217, saturation: 100 },
  green: { hue: 141, saturation: 56 },
  yellow: { hue: 44, saturation: 100 },
  turquoise: { hue: 174, saturation: 70 },
  rogue: { hue: 0, saturation: 100 },
  unaligned: { hue: 220, saturation: 10 },
};

export function stationTheme(allegiance: FactionId | null): StationTheme {
  const key = allegiance ?? 'unaligned';
  const { hue, saturation } = HUES[key];

  const faction = allegiance ? FACTIONS[allegiance] : null;

  return {
    operator: faction?.name ?? 'Independent',
    abbreviation: faction ? (faction.abbreviation ?? null) : 'IND',

    accent: `hsl(${hue} ${saturation}% 66%)`,
    // Pulled well down in saturation: a whole panel of text at the accent's
    // strength is a headache, and the colour is doing its job on the chrome.
    text: `hsl(${hue} ${Math.round(saturation * 0.35)}% 88%)`,
    dim: `hsla(${hue} ${Math.round(saturation * 0.3)}% 88% / 0.45)`,
    edge: `hsla(${hue} ${saturation}% 66% / 0.35)`,
    backing: `hsla(${hue} ${Math.round(saturation * 0.4)}% 6% / 0.86)`,
    active: `hsla(${hue} ${saturation}% 66% / 0.24)`,
  };
}
